import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, ArrowRight, Stethoscope, Shield, GraduationCap, Award, FileText } from 'lucide-react';

const examDetails = {
  code: 'CBEAD',
  title: 'Competency-Based Examination in Aesthetic Dentistry & Facial Aesthetics',
  eligibility: 'Registered Dental Practitioners with valid dental license',
  fee: '₹32,000',
  components: [
    { name: 'Written Examination', desc: 'MCQ-based assessment of theoretical knowledge' },
    { name: 'OSCE Assessment', desc: 'Structured clinical skill evaluation stations' },
    { name: 'Case Log Review', desc: 'Documentation of supervised clinical cases' },
    { name: 'Viva Voce', desc: 'Oral examination and case defense' },
  ],
  competencies: [
    'Perioral injectable competency (Botulinum toxin, Dermal fillers)',
    'Smile harmony and facial proportion assessment',
    'Lip enhancement and perioral rejuvenation',
    'Botox applications for bruxism and TMJ',
    'Integration of dental and facial aesthetics',
    'Patient safety and emergency protocols',
  ],
};

export default function DentalFacultyPage() {
  return (
    <div data-testid="dental-faculty-page">
      {/* Hero Section */}
      <section className="bg-[#1B2A4A] py-16 md:py-20">
        <div className="imcac-container">
          <p className="imcac-overline mb-3">Division of Competency-Based Examination & Certification</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight font-heading">
            Faculty of Dental Clinical Competency
          </h1>
          <div className="h-1 w-20 bg-[#C5A572] mt-5" />
          <p className="text-lg text-slate-300 mt-4 max-w-3xl">
            Competency-based certification pathway for Dental Practitioners in aesthetic dentistry and facial aesthetics.
          </p>
        </div>
      </section>

      {/* Faculty Overview */}
      <section className="imcac-section bg-white">
        <div className="imcac-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-7">
              <p className="imcac-overline mb-3">Faculty Overview</p>
              <h2 className="text-3xl font-semibold text-[#1B2A4A] font-heading mb-6">
                Bridging Dental Excellence with Facial Aesthetics
              </h2>
              <p className="text-base text-slate-600 leading-relaxed mb-6">
                The Faculty of Dental Clinical Competency provides a structured certification pathway for 
                dental professionals seeking to expand their expertise into facial aesthetics. This faculty 
                recognizes the unique position of dentists in understanding facial anatomy, oral-facial 
                relationships, and perioral aesthetic procedures.
              </p>

              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-[#F8FAFC] p-4 rounded-sm border border-slate-200">
                  <Award className="h-6 w-6 text-[#C5A572] mb-2" />
                  <p className="text-sm font-semibold text-[#1B2A4A]">Board Certification</p>
                  <p className="text-xs text-slate-500">Board Certified in Aesthetic Dentistry & Facial Aesthetics (IMCAC – CBEAD)</p>
                </div>
                <div className="bg-[#F8FAFC] p-4 rounded-sm border border-slate-200">
                  <FileText className="h-6 w-6 text-[#C5A572] mb-2" />
                  <p className="text-sm font-semibold text-[#1B2A4A]">Scope of Practice</p>
                  <p className="text-xs text-slate-500">Perioral region & dental-facial integration</p>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-sm p-4">
                <div className="flex items-start gap-3">
                  <Shield className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-amber-800 mb-1">Regulatory Compliance</p>
                    <p className="text-sm text-amber-700">
                      Certification is subject to national dental council regulations. Candidates must verify 
                      that aesthetic procedures are within their permitted scope of practice in their jurisdiction.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5">
              <img
                src="https://images.unsplash.com/photo-1606811841689-23dfddce3e95?auto=format&fit=crop&w=800&q=80"
                alt="Dental Professional"
                className="w-full h-72 object-cover rounded-sm shadow-md"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CBEAD Examination */}
      <section className="imcac-section bg-[#F8FAFC]">
        <div className="imcac-container">
          <div className="text-center mb-12">
            <Badge className="bg-[#C5A572] text-white text-sm mb-3">{examDetails.code}</Badge>
            <h2 className="text-3xl font-semibold text-[#1B2A4A] font-heading">{examDetails.title}</h2>
            <p className="text-sm text-slate-500 mt-2">For {examDetails.eligibility}</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Examination Components */}
            <Card className="imcac-card">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-[#1B2A4A] font-heading mb-4 flex items-center gap-2">
                  <Stethoscope className="h-5 w-5 text-[#C5A572]" />
                  Examination Components
                </h3>
                <div className="space-y-4">
                  {examDetails.components.map((comp, i) => (
                    <div key={i} className="flex items-start gap-3 pb-3 border-b border-slate-100 last:border-0">
                      <div className="w-6 h-6 bg-[#1B2A4A] rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-xs text-white font-bold">{i + 1}</span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-[#1B2A4A]">{comp.name}</p>
                        <p className="text-xs text-slate-500">{comp.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Core Competencies */}
            <Card className="imcac-card">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-[#1B2A4A] font-heading mb-4 flex items-center gap-2">
                  <GraduationCap className="h-5 w-5 text-[#C5A572]" />
                  Core Competencies Assessed
                </h3>
                <div className="space-y-3">
                  {examDetails.competencies.map((comp, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-[#C5A572] flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-slate-600">{comp}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-8">
            <Link to="/dental-faculty/aesthetic-dentistry" data-testid="explore-cbead-link">
              <Button className="bg-[#1B2A4A] hover:bg-[#152039] text-white rounded-sm px-8">
                View Full CBEAD Details <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Legal Disclaimer */}
      <section className="py-10 bg-[#F8FAFC]">
        <div className="imcac-container">
          <div className="bg-white border border-slate-200 rounded-sm p-6">
            <div className="flex items-start gap-3">
              <Shield className="h-5 w-5 text-[#C5A572] flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-[#1B2A4A] mb-2">Legal Disclaimer</p>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Certification under the Faculty of Dental Clinical Competency validates assessed competencies 
                  within the dental-aesthetic domain and does not grant medical licensure. All candidates must 
                  comply with the regulatory requirements of their respective Dental Council and ensure that 
                  aesthetic procedures fall within their permitted scope of practice in their jurisdiction.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
